import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { AuthProvider, ToastProvider } from '@vineyard/shared'
import ToastList from '@/components/ToastList'
import RouteGuard from '@/components/RouteGuard'
import AppLayout from '@/layouts/AppLayout'
import LoadingSpinner from '@/components/LoadingSpinner'

// Public pages — lazy loaded to reduce initial bundle
const LandingPage = lazy(() => import('@/pages/LandingPage'))
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const SignupPage = lazy(() => import('@/pages/SignupPage'))
const AuthCallbackPage = lazy(() => import('@/pages/AuthCallbackPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'))

// Protected pages — lazy loaded (Tiptap, Typst WASM only pulled in when accessed)
const HomePage = lazy(() => import('@/pages/HomePage'))
const BoardPage = lazy(() => import('@/pages/board/BoardPage'))
const BoardDetailPage = lazy(() => import('@/pages/board/BoardDetailPage'))
const BoardNewPage = lazy(() => import('@/pages/board/BoardNewPage'))
const SermonsPage = lazy(() => import('@/pages/sermons/SermonsPage'))
const SermonDetailPage = lazy(() => import('@/pages/sermons/SermonDetailPage'))
const SermonNewPage = lazy(() => import('@/pages/sermons/SermonNewPage'))
const GroupsPage = lazy(() => import('@/pages/groups/GroupsPage'))
const GroupDetailPage = lazy(() => import('@/pages/groups/GroupDetailPage'))
const GroupNewPage = lazy(() => import('@/pages/groups/GroupNewPage'))
const BulletinPage = lazy(() => import('@/pages/BulletinPage'))
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'))
const SearchPage = lazy(() => import('@/pages/SearchPage'))
const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage'))
const ProfileEditPage = lazy(() => import('@/pages/profile/ProfileEditPage'))
const UserProfilePage = lazy(() => import('@/pages/profile/UserProfilePage'))
const ChurchPage = lazy(() => import('@/pages/church/ChurchPage'))
const ChurchPendingPage = lazy(() => import('@/pages/church/ChurchPendingPage'))
const ChurchRegisterPage = lazy(() => import('@/pages/church/ChurchRegisterPage'))
const ChurchSettingsPage = lazy(() => import('@/pages/church/ChurchSettingsPage'))
const ChurchMemberSettingsPage = lazy(() => import('@/pages/church/ChurchMemberSettingsPage'))
const ChurchMembersPage = lazy(() => import('@/pages/church/ChurchMembersPage'))
const CalendarPage = lazy(() => import('@/pages/calendar/CalendarPage'))
const CalendarEventDetailPage = lazy(() => import('@/pages/calendar/CalendarEventDetailPage'))
const CalendarEventNewPage = lazy(() => import('@/pages/calendar/CalendarEventNewPage'))
const ThirdPartyLicensesPage = lazy(() => import('@/pages/ThirdPartyLicensesPage'))
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'))
const TermsPage = lazy(() => import('@/pages/TermsPage'))

function RouteTracker() {
  const location = useLocation()
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', { page_path: location.pathname + location.search })
    }
  }, [location])
  return null
}

export default function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <RouteTracker />
      <ToastProvider>
        <AuthProvider>
          <RouteGuard>
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/third-party-licenses" element={<ThirdPartyLicensesPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />

                {/* Protected routes */}
                <Route element={<AppLayout />}>
                  <Route path="/home" element={<HomePage />} />
                  <Route path="/board" element={<BoardPage />} />
                  <Route path="/board/:id" element={<BoardDetailPage />} />
                  <Route path="/board/new" element={<BoardNewPage />} />
                  <Route path="/sermons" element={<SermonsPage />} />
                  <Route path="/sermons/:id" element={<SermonDetailPage />} />
                  <Route path="/sermons/new" element={<SermonNewPage />} />
                  <Route path="/groups" element={<GroupsPage />} />
                  <Route path="/groups/:id" element={<GroupDetailPage />} />
                  <Route path="/groups/new" element={<GroupNewPage />} />
                  <Route path="/calendar" element={<CalendarPage />} />
                  <Route path="/calendar/:id" element={<CalendarEventDetailPage />} />
                  <Route path="/calendar/new" element={<CalendarEventNewPage />} />
                  <Route path="/bulletin" element={<BulletinPage />} />
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/search" element={<SearchPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/profile/edit" element={<ProfileEditPage />} />
                  <Route path="/users/:userId" element={<UserProfilePage />} />
                  <Route path="/church" element={<ChurchPage />} />
                  <Route path="/church/pending" element={<ChurchPendingPage />} />
                  <Route path="/church/register" element={<ChurchRegisterPage />} />
                  <Route path="/church/settings" element={<ChurchSettingsPage />} />
                  <Route path="/church/members" element={<ChurchMembersPage />} />
                  <Route path="/church/member-settings" element={<ChurchMemberSettingsPage />} />
                </Route>
              </Routes>
            </Suspense>
          </RouteGuard>
          <ToastList />
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  )
}
